import { STATUS, DATE_FORMATS } from './constants';

/**
 * Format a date string to a human-readable format
 * @param {string} dateString - ISO date string
 * @param {object} options - Intl.DateTimeFormat options
 * @returns {string} Formatted date string
 */
export const formatDate = (dateString, options = {}) => {
  const defaultOptions = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true
  };
  
  const mergedOptions = { ...defaultOptions, ...options };
  const date = new Date(dateString);
  
  return date.toLocaleString('en-US', mergedOptions);
};

/**
 * Get color for status chip based on status value
 * @param {string} status - Status value
 * @returns {string} MUI color value
 */
export const getStatusColor = (status) => {
  if (!status) return 'default';
  
  const statusLower = status.toLowerCase();
  
  switch (statusLower) {
    case STATUS.COMPLETED.toLowerCase():
    case STATUS.APPROVED.toLowerCase():
      return 'success';
    case STATUS.PENDING.toLowerCase():
      return 'warning';
    case STATUS.ARRIVED.toLowerCase():
      return 'info';
    case STATUS.CANCELLED.toLowerCase():
    case STATUS.DENIED.toLowerCase():
      return 'error';
    default:
      console.log(`Unknown status: ${status}`);
      return 'default';
  }
};

/**
 * Capitalize the first letter of each word in a string
 * @param {string} str - String to capitalize
 * @returns {string} Capitalized string
 */
export const capitalizeWords = (str) => {
  if (!str) return '';
  
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Truncate a string to a specified length and add ellipsis
 * @param {string} str - String to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated string
 */
export const truncateString = (str, maxLength = 30) => {
  if (!str || str.length <= maxLength) return str;
  
  return `${str.substring(0, maxLength)}...`;
};

/**
 * Format a phone number to (XXX) XXX-XXXX format
 * @param {string} phoneNumber - Phone number to format
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // Check if the input is of correct length
  const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
  
  if (match) {
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  }
  
  return phoneNumber;
};

/**
 * Extract initials from a name
 * @param {string} name - Full name
 * @param {number} count - Number of initials to extract (default: 2)
 * @returns {string} Initials
 */
export const getInitials = (name, count = 2) => {
  if (!name) return '';
  
  return name
    .split(' ')
    .slice(0, count)
    .map(part => part.charAt(0).toUpperCase())
    .join('');
};

/**
 * Check if a value is empty (null, undefined, empty string, empty array, or empty object)
 * @param {*} value - Value to check
 * @returns {boolean} True if empty, false otherwise
 */
export const isEmpty = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && value.trim() === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && Object.keys(value).length === 0) return true;
  
  return false;
};

/**
 * Format a date using a predefined format from constants
 * @param {string} dateString - ISO date string
 * @param {string} formatType - Format type from DATE_FORMATS
 * @returns {string} Formatted date string
 */
export const formatDateByType = (dateString, formatType = 'DISPLAY') => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  const format = DATE_FORMATS[formatType];
  
  // This is a simplified implementation
  // In a real app, you might use a library like date-fns or moment.js
  
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  
  // Simple formatting based on format type
  switch (formatType) {
    case 'SHORT_DATE':
      return `${month.toString().padStart(2, '0')}/${day.toString().padStart(2, '0')}/${year}`;
    case 'TIME_ONLY':
      const hour12 = hours % 12 || 12;
      const ampm = hours >= 12 ? 'PM' : 'AM';
      return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
    case 'API':
      return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')} ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    case 'DISPLAY':
    default:
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const hour12Display = hours % 12 || 12;
      const ampmDisplay = hours >= 12 ? 'PM' : 'AM';
      return `${monthNames[date.getMonth()]} ${day}, ${year} ${hour12Display}:${minutes.toString().padStart(2, '0')} ${ampmDisplay}`;
  }
};
